// SPC X-bar / R 차트 — 명세서 FR3 "SPC 분석을 통해 품질 데이터를 시각화"
import { useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Card } from '../components/Card'
import { Skeleton, SkeletonRows } from '../components/Skeleton'
import { api } from '../lib/api'
import { useToast } from '../components/Toast'
import { useGlobalReveal } from '../hooks/useReveal'
import { useT } from '../lib/i18n'

interface Characteristic {
  characteristic_id: string
  item_code: string
  name: string
  uom: string
  target: number
  usl: number
  lsl: number
  subgroup_size: number
}

interface Subgroup { subgroup_id: string; values: number[]; x_bar: number; range_val: number; collected_at: string }

interface Chart {
  n: number; k: number; xDoubleBar: number; rBar: number; sigmaHat: number
  xbarUCL: number; xbarLCL: number; rUCL: number; rLCL: number
  cp?: number; cpk?: number
  outOfControl: { type: 'X' | 'R'; idx: number; value: number; rule: string }[]
  subgroups: { x_bar: number; range_val: number }[]
}

interface ChartResp {
  characteristic: Characteristic
  subgroups: Subgroup[]
  chart: Chart | null
  warning?: string
}

function XbarChart({ chart, ch }: { chart: Chart; ch: Characteristic }) {
  const W = 720, H = 220, padL = 56, padR = 24, padT = 18, padB = 32
  const innerW = W - padL - padR
  const innerH = H - padT - padB
  const yMin = Math.min(ch.lsl, chart.xbarLCL, ...chart.subgroups.map((s) => s.x_bar))
  const yMax = Math.max(ch.usl, chart.xbarUCL, ...chart.subgroups.map((s) => s.x_bar))
  const yPad = (yMax - yMin) * 0.08 || 0.001
  const y0 = yMin - yPad
  const y1 = yMax + yPad
  const x = (i: number) => padL + (chart.k <= 1 ? innerW / 2 : (i / (chart.k - 1)) * innerW)
  const y = (v: number) => padT + (1 - (v - y0) / (y1 - y0)) * innerH
  const ooc = new Set(chart.outOfControl.filter((o) => o.type === 'X').map((o) => o.idx))
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="X-bar 차트" style={{ width: '100%', height: 'auto' }}>
      {/* 가이드 라인 */}
      <line x1={padL} y1={y(chart.xbarUCL)} x2={W - padR} y2={y(chart.xbarUCL)} stroke="var(--color-danger)" strokeDasharray="4 4" />
      <line x1={padL} y1={y(chart.xbarLCL)} x2={W - padR} y2={y(chart.xbarLCL)} stroke="var(--color-danger)" strokeDasharray="4 4" />
      <line x1={padL} y1={y(chart.xDoubleBar)} x2={W - padR} y2={y(chart.xDoubleBar)} stroke="var(--color-accent)" strokeWidth={1.5} />
      <line x1={padL} y1={y(ch.target)} x2={W - padR} y2={y(ch.target)} stroke="var(--color-ink-3)" strokeDasharray="1 4" opacity={0.5} />
      {/* 데이터 라인 */}
      <polyline
        fill="none"
        stroke="var(--color-ink)"
        strokeWidth={1.5}
        points={chart.subgroups.map((s, i) => `${x(i)},${y(s.x_bar)}`).join(' ')}
      />
      {chart.subgroups.map((s, i) => (
        <circle key={i} cx={x(i)} cy={y(s.x_bar)} r={ooc.has(i) ? 5 : 3}
          fill={ooc.has(i) ? 'var(--color-danger)' : 'var(--color-ink)'}
          stroke={ooc.has(i) ? 'var(--color-danger)' : 'none'}
        >
          <title>#{i + 1} X̄ = {s.x_bar.toFixed(4)}</title>
        </circle>
      ))}
      {/* 축 라벨 */}
      <text x={padL - 6} y={y(chart.xbarUCL)} textAnchor="end" dominantBaseline="middle" fontSize="11" fill="var(--color-danger)">UCL {chart.xbarUCL.toFixed(4)}</text>
      <text x={padL - 6} y={y(chart.xDoubleBar)} textAnchor="end" dominantBaseline="middle" fontSize="11" fill="var(--color-accent)">X̿ {chart.xDoubleBar.toFixed(4)}</text>
      <text x={padL - 6} y={y(chart.xbarLCL)} textAnchor="end" dominantBaseline="middle" fontSize="11" fill="var(--color-danger)">LCL {chart.xbarLCL.toFixed(4)}</text>
      <text x={padL} y={H - 8} fontSize="11" fill="var(--color-ink-3)">subgroup #1</text>
      <text x={W - padR} y={H - 8} textAnchor="end" fontSize="11" fill="var(--color-ink-3)">#{chart.k}</text>
    </svg>
  )
}

function RChart({ chart }: { chart: Chart }) {
  const W = 720, H = 180, padL = 56, padR = 24, padT = 18, padB = 32
  const innerW = W - padL - padR
  const innerH = H - padT - padB
  const yMax = Math.max(chart.rUCL, ...chart.subgroups.map((s) => s.range_val))
  const y0 = 0
  const y1 = yMax * 1.1 || 0.001
  const x = (i: number) => padL + (chart.k <= 1 ? innerW / 2 : (i / (chart.k - 1)) * innerW)
  const y = (v: number) => padT + (1 - (v - y0) / (y1 - y0)) * innerH
  const ooc = new Set(chart.outOfControl.filter((o) => o.type === 'R').map((o) => o.idx))
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="R 차트" style={{ width: '100%', height: 'auto' }}>
      <line x1={padL} y1={y(chart.rUCL)} x2={W - padR} y2={y(chart.rUCL)} stroke="var(--color-danger)" strokeDasharray="4 4" />
      <line x1={padL} y1={y(chart.rBar)} x2={W - padR} y2={y(chart.rBar)} stroke="var(--color-accent)" strokeWidth={1.5} />
      <polyline
        fill="none" stroke="var(--color-ink)" strokeWidth={1.5}
        points={chart.subgroups.map((s, i) => `${x(i)},${y(s.range_val)}`).join(' ')}
      />
      {chart.subgroups.map((s, i) => (
        <circle key={i} cx={x(i)} cy={y(s.range_val)} r={ooc.has(i) ? 5 : 3}
          fill={ooc.has(i) ? 'var(--color-danger)' : 'var(--color-ink)'}
        >
          <title>#{i + 1} R = {s.range_val.toFixed(4)}</title>
        </circle>
      ))}
      <text x={padL - 6} y={y(chart.rUCL)} textAnchor="end" dominantBaseline="middle" fontSize="11" fill="var(--color-danger)">UCL {chart.rUCL.toFixed(4)}</text>
      <text x={padL - 6} y={y(chart.rBar)} textAnchor="end" dominantBaseline="middle" fontSize="11" fill="var(--color-accent)">R̄ {chart.rBar.toFixed(4)}</text>
    </svg>
  )
}

export function SpcPage() {
  useGlobalReveal()
  const toast = useToast()
  const { t } = useT()
  const [list, setList] = useState<Characteristic[] | null>(null)
  const [active, setActive] = useState<string | null>(null)
  const [data, setData] = useState<ChartResp | null>(null)

  useEffect(() => {
    api.get<Characteristic[]>('/spc/characteristics')
      .then((d) => {
        setList(d)
        if (d[0]) setActive(d[0].characteristic_id)
      })
      .catch((e) => { setList([]); toast.push('error', e?.message ?? '조회 실패') })
  }, [toast])

  useEffect(() => {
    if (!active) return
    setData(null)
    api.get<ChartResp>(`/spc/chart/${active}`)
      .then(setData)
      .catch((e) => toast.push('error', e?.message ?? '차트 로드 실패'))
  }, [active, toast])

  const capability = useMemo(() => {
    if (!data?.chart?.cp || !data?.chart?.cpk) return null
    const cpk = data.chart.cpk
    const grade =
      cpk >= 1.67 ? { label: '뛰어남', color: 'success' } :
      cpk >= 1.33 ? { label: '적합', color: 'info' } :
      cpk >= 1.00 ? { label: '경계', color: 'warn' } :
      { label: '부적합', color: 'danger' }
    return { cp: data.chart.cp, cpk, grade }
  }, [data])

  return (
    <main id="main" className="container stack" style={{ paddingBlock: '2rem' }}>
      <h1 className="page-title reveal">
        <FontAwesomeIcon icon="chart-area" /> {t('spc.title')}
      </h1>
      <p className="muted reveal">
        {t('spc.intro', { n: String(data?.chart?.n ?? '·') })}
      </p>

      <Card glass tilt={false} spotlight={false} className="reveal" as="section">
        <div className="field" style={{ maxWidth: 480, marginBottom: 0 }}>
          <label htmlFor="ch">{t('spc.selectCh')}</label>
          <select id="ch" value={active ?? ''} onChange={(e) => setActive(e.target.value)} disabled={!list}>
            {!list && <option>로드 중…</option>}
            {list?.map((c) => (
              <option key={c.characteristic_id} value={c.characteristic_id}>
                {c.characteristic_id} — {c.item_code} {c.name} ({c.uom})
              </option>
            ))}
          </select>
        </div>
      </Card>

      {!active ? (
        <Card glass tilt={false} spotlight={false} className="reveal">
          <div className="empty-state" role="status">
            <FontAwesomeIcon icon="chart-area" className="icon-lg" />
            <p className="muted">상단의 측정 항목을 선택하면 X̄–R 차트와 Cp/Cpk가 표시됩니다.</p>
          </div>
        </Card>
      ) : !data && active ? (
        <Card glass tilt={false} spotlight={false} className="reveal">
          <Skeleton h={260} />
        </Card>
      ) : data?.warning ? (
        <Card glass tilt={false} spotlight={false} className="reveal">
          <div className="empty-state" role="status">
            <FontAwesomeIcon icon="chart-area" className="icon-lg" />
            <p className="muted">{data.warning}</p>
          </div>
        </Card>
      ) : data?.chart ? (
        <>
          <Card glass tilt={false} spotlight={false} className="reveal" as="section">
            <div className="row" style={{ gap: '1.2rem', flexWrap: 'wrap' }}>
              <div><span className="muted">규격</span>{' '}<span className="mono">{data.characteristic.lsl} – {data.characteristic.target} – {data.characteristic.usl} {data.characteristic.uom}</span></div>
              <div><span className="muted">X̿</span> <span className="mono">{data.chart.xDoubleBar.toFixed(4)}</span></div>
              <div><span className="muted">R̄</span> <span className="mono">{data.chart.rBar.toFixed(4)}</span></div>
              <div><span className="muted">σ̂</span> <span className="mono">{data.chart.sigmaHat.toFixed(5)}</span></div>
              {capability && (
                <>
                  <div><span className="muted">Cp</span> <span className="mono">{capability.cp.toFixed(2)}</span></div>
                  <div>
                    <span className="muted">Cpk</span>{' '}
                    <span className="mono">{capability.cpk.toFixed(2)}</span>{' '}
                    <span className={`badge ${capability.grade.color}`}>{capability.grade.label}</span>
                  </div>
                </>
              )}
              {data.chart.outOfControl.length > 0 && (
                <span className="badge danger">
                  <FontAwesomeIcon icon="triangle-exclamation" /> 관리이탈 {data.chart.outOfControl.length}건
                </span>
              )}
            </div>
          </Card>

          <Card glass tilt={false} spotlight={false} className="reveal" as="section" aria-labelledby="xbar-h">
            <h2 id="xbar-h" style={{ fontSize: 'var(--fs-lg)' }}>X̄ 차트</h2>
            <XbarChart chart={data.chart} ch={data.characteristic} />
          </Card>

          <Card glass tilt={false} spotlight={false} className="reveal" as="section" aria-labelledby="r-h">
            <h2 id="r-h" style={{ fontSize: 'var(--fs-lg)' }}>R 차트</h2>
            <RChart chart={data.chart} />
          </Card>

          {data.chart.outOfControl.length > 0 && (
            <Card glass tilt={false} spotlight={false} className="reveal" as="section">
              <h2 style={{ fontSize: 'var(--fs-lg)' }}>관리이탈 상세</h2>
              <ul style={{ paddingLeft: 0, listStyle: 'none', display: 'grid', gap: '0.4rem' }}>
                {data.chart.outOfControl.map((o, i) => (
                  <li key={i} style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                    <span className="badge danger">{o.type}</span>
                    <span className="mono">subgroup #{o.idx + 1}</span>
                    <span>{o.rule}</span>
                    <span className="mono" style={{ marginLeft: 'auto' }}>{o.value.toFixed(4)}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      ) : (
        <Card glass tilt={false} spotlight={false} className="reveal">
          <div className="empty-state" role="status">
            <FontAwesomeIcon icon="chart-area" className="icon-lg" />
            <p className="muted">측정값이 부분집단을 이루기에 충분하지 않습니다 (n=5 필요).</p>
          </div>
        </Card>
      )}
    </main>
  )
}
